import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { getDppContent } from 'Api/DppApi';
import { getPlans } from 'State/Plans/PlansActions';
import { fetchSubscriptions } from 'State/Subscriptions/SubscriptionsActions';
import { fetchPayments } from 'State/Payments/PaymentActions';
import { CancelButton, PillButton } from 'Components/Fields/Buttons';
import { RedfastZone } from 'Components/RedfastZone';
import { CANCEL_INTERRUPT, CANCEL_INTERRUPT_SUCCESS } from 'Utils/Constants';
import BrazeContentCard from 'Components/BrazeContent/BrazeContentCard';
import { hasBrazeContentCard } from 'Utils/Braze';
import { setContentCards } from 'State/BrazeContentCards/ContentCardsActions';
import './CancelPlanContainer.css';

export const CancelPlanContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dppChecked, setDppChecked] = useState(false);
  const [cancelPreview, setCancelPreview] = useState('');
  // Response can be "success", "error" or empty string ''
  const [cancelPromoRes, setCancelPromoRes] = useState('');
  const { defaultConfig, routes, screens } = useSelector((s) => s.appConfig);
  const { getPlansSuccess } = useSelector((s) => s.plans);
  const { isSuccess, hasActiveSubscription } = useSelector((s) => s.subscription);
  const contentCards = useSelector((s) => s.contentCards.allContentCards);
  const { cancelPlanPreview } = screens;
  const { entitlementId } = defaultConfig;

  useEffect(() => {
    getDppContent(cancelPlanPreview).then((member) => {
      if (member && member.length > 0) {
        const [previewPanel = {}] = member;
        const { headline = '' } = previewPanel;
        setCancelPreview(headline);
        setDppChecked(true);
      }
    });
  }, [cancelPlanPreview]);

  useEffect(() => {
    if (!isSuccess) {
      dispatch(fetchSubscriptions());
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    dispatch(fetchPayments(entitlementId));
  }, [dispatch, entitlementId]);

  useEffect(() => {
    const successContentCard = hasBrazeContentCard(CANCEL_INTERRUPT_SUCCESS);
    if (successContentCard && cancelPromoRes === 'success') {
      // remove Cancel Interrupt promo content card from redux store
      const filteredContentCards = contentCards.filter((banner) => banner.extras.zone !== CANCEL_INTERRUPT);
      dispatch(setContentCards(filteredContentCards));
      setTimeout(() => {
        navigate(routes.myAccount);
      }, 10000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelPromoRes, dispatch, navigate, routes.myAccount]);

  const isLoading = useMemo(() => !getPlansSuccess || !dppChecked, [getPlansSuccess, dppChecked]);

  const onCancel = async () => {
    return navigate(routes.cancelSurvey);
  };

  if (!hasActiveSubscription) {
    return <Navigate replace to={routes.myAccount} />;
  }

  return (
    <CenterFrameLayout
      title={cancelPreview || "We're sad to see you go"}
      showLoader={isLoading}
      customClass="cancelPlanContainer"
    >
      <div className="centerFormWide">
        <RedfastZone name="CancelInterruptZone" />
        {!cancelPromoRes && <BrazeContentCard zone={CANCEL_INTERRUPT} setCancelPromoRes={setCancelPromoRes} />}
        {cancelPromoRes === 'success' && (
          <BrazeContentCard zone={CANCEL_INTERRUPT_SUCCESS} setCancelPromoRes={setCancelPromoRes} withCloseButton />
        )}
        {cancelPromoRes === 'error' && (
          <div className="errorMessage">There was an error processing your request, please try again</div>
        )}
        <PillButton
          modernLayout
          className="cancelSubscriptionBtn"
          text="Cancel Subscription"
          onClick={() => onCancel('Cancel my subscription')}
        />
      </div>
    </CenterFrameLayout>
  );
};
